import {isSameDay, subDays} from 'date-fns'
import {ACTIVITY_TAG_KEYS} from '../constants/initiativeActivityConstants'
import type {TFormatDate} from '../hooks/useFormatDate'
import {INITIATIVE} from '../Translation/English/initiative'
import type {
  IActivityLogRow,
  IInitiativeActivityItem,
  TInitiativeLogType,
} from '../types/initiativeActivityTypes'
import {getAvatarLetters} from './avatarLetterUtil'

interface IToActivityLogRowsOptions {
  formatDate: TFormatDate
  wt: (key: string) => string
  /** Injected so day labels are testable; defaults to the current time. */
  now?: Date
}

const isLogType = (type: string): type is TInitiativeLogType =>
  Object.prototype.hasOwnProperty.call(ACTIVITY_TAG_KEYS, type)

/** "Today" / "Yesterday" in the viewer's local calendar, else a medium date. */
const getDayLabel = (
  date: Date,
  now: Date,
  formatDate: TFormatDate,
  wt: (key: string) => string,
): string => {
  if (isSameDay(date, now)) return wt(INITIATIVE.today)
  if (isSameDay(date, subDays(now, 1))) return wt(INITIATIVE.yesterday)
  return formatDate(date, 'medium')
}

/**
 * Maps API items onto WuActivityLog rows, keeping their order. WuActivityLog
 * groups on `date`, so rows from different pages that share a day merge into
 * one group. `description` passes through untouched: the server already embeds
 * a block reason in it, and WuActivityLog renders it as text, not HTML.
 */
const toActivityLogRows = (
  items: IInitiativeActivityItem[],
  {formatDate, wt, now = new Date()}: IToActivityLogRowsOptions,
): IActivityLogRow[] =>
  // Day label and time both come from `createdAt`, in the viewer's browser
  // timezone. The server's `date`/`time` are UTC strings and are never read.
  items.flatMap(item => {
    const createdAt = new Date(item.createdAt)
    // WuActivityLog silently drops a row whose date is empty. Drop a missing
    // or unparseable createdAt here instead, where it is visible, rather than
    // render a bogus time.
    if (!item.createdAt || Number.isNaN(createdAt.getTime())) return []
    return [
      {
        id: item.id,
        date: getDayLabel(createdAt, now, formatDate, wt),
        time: formatDate(createdAt, 'time'),
        userName: item.userName,
        userInitials: getAvatarLetters(item.userName) || '?',
        description: item.description,
        // A type the backend added before the client knows it: no tag, still a row.
        tags: isLogType(item.type) ? [wt(ACTIVITY_TAG_KEYS[item.type])] : [],
      },
    ]
  })

export const activityLogUtil = {toActivityLogRows, getDayLabel} as const
